import { useMemo, useState } from "react";
import type { Conversation, PresenceKind } from "@swift/shared";
import { useStore } from "../lib/store";
import { api, uploadFile } from "../lib/api";

/**
 * "Delen met Swift" — modale kiezer die verschijnt zodra de gebruiker
 * vanuit een andere app inhoud naar Swift deelt (Android share-sheet).
 * Toont de gedeelde tekst/bestanden, een bijschrift-veld en een
 * doorzoekbare multi-select van kanalen en gesprekken — vergelijkbaar
 * met WhatsApp's deel-scherm. Bij versturen gaat naar elk gekozen doel
 * één bericht; bestanden worden per doel apart geüpload omdat de server
 * een bijlage aan precies één bericht koppelt.
 *
 * De data komt uit store.pendingShare; rendert niets als die null is.
 */

type Target =
  | { key: string; kind: "conv"; convId: number; name: string; sub: string; presence?: PresenceKind }
  | { key: string; kind: "user"; userId: number; name: string; sub: string; presence: PresenceKind };

function presenceColor(kind: PresenceKind): string {
  switch (kind) {
    case "online":
      return "bg-brand-green";
    case "away":
      return "bg-yellow-500";
    case "busy":
      return "bg-red-500";
    default:
      return "bg-slate-500";
  }
}

function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SharePicker() {
  const share = useStore((s) => s.pendingShare);
  const setPendingShare = useStore((s) => s.setPendingShare);
  const conversations = useStore((s) => s.conversations);
  const order = useStore((s) => s.conversationOrder);
  const users = useStore((s) => s.users);
  const me = useStore((s) => s.me);
  const upsertConversation = useStore((s) => s.upsertConversation);
  const setActive = useStore((s) => s.setActiveConversation);

  const [caption, setCaption] = useState(share?.text ?? "");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);

  // Bestaande DM per andere-gebruiker, zodat we een al lopende conversatie
  // hergebruiken in plaats van een tweede aan te maken.
  const dmByOther = useMemo(() => {
    const m = new Map<number, Conversation>();
    for (const id of order) {
      const c = conversations[id];
      if (!c || c.kind !== "dm") continue;
      const otherId = c.member_ids.find((uid) => uid !== me?.id);
      if (otherId !== undefined) m.set(otherId, c);
    }
    return m;
  }, [order, conversations, me?.id]);

  const channels = useMemo<Target[]>(() => {
    return order
      .map((id) => conversations[id])
      .filter((c): c is Conversation => c !== undefined && c.kind === "channel")
      .map((c) => ({
        key: `conv:${c.id}`,
        kind: "conv" as const,
        convId: c.id,
        name: c.name ?? "kanaal",
        sub: "Kanaal",
      }));
  }, [order, conversations]);

  // Mensen: alleen niet-offline collega's (exclusief jezelf). De Composer
  // staat sturen naar offline DM-partners ook niet toe, dus die thuis
  // laten houdt de kiezer kort en voorkomt dood-lopende selecties. Heeft
  // de persoon al een DM, dan verwijst de target naar die conversatie;
  // anders maken we 'm bij verzenden.
  const people = useMemo<Target[]>(() => {
    return Object.values(users)
      .filter((u) => u.id !== me?.id && u.status_kind !== "offline")
      .sort((a, b) => a.display_name.localeCompare(b.display_name))
      .map((u) => {
        const dm = dmByOther.get(u.id);
        return dm
          ? {
              key: `conv:${dm.id}`,
              kind: "conv" as const,
              convId: dm.id,
              name: u.display_name,
              sub: `@${u.username}`,
              presence: u.status_kind,
            }
          : {
              key: `user:${u.id}`,
              kind: "user" as const,
              userId: u.id,
              name: u.display_name,
              sub: `@${u.username}`,
              presence: u.status_kind,
            };
      });
  }, [users, me?.id, dmByOther]);

  const filter = (list: Target[]): Target[] => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((t) => `${t.name} ${t.sub}`.toLowerCase().includes(q));
  };
  const shownChannels = filter(channels);
  const shownPeople = filter(people);

  if (!share) return null;

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function cancel() {
    setPendingShare(null);
  }

  async function doSend() {
    if (!share || selected.size === 0 || sending) return;
    setSending(true);

    // Alle gekozen targets terugvinden in de actuele lijst.
    const all = [...channels, ...people];
    const targets = [...selected]
      .map((k) => all.find((t) => t.key === k))
      .filter((t): t is Target => t !== undefined);

    const body = caption.trim();
    let lastConvId: number | null = null;
    const succeeded = new Set<string>();
    const failed: Array<{ name: string; reason: string }> = [];

    try {
      for (const t of targets) {
        setProgress(`Versturen naar ${t.name}…`);
        try {
          // Doel-conversatie bepalen (DM eventueel aanmaken).
          let convId: number;
          if (t.kind === "conv") {
            convId = t.convId;
          } else {
            const existing = dmByOther.get(t.userId);
            if (existing) {
              convId = existing.id;
            } else {
              const { conversation } = await api.createConversation({
                kind: "dm",
                other_user_id: t.userId,
              });
              upsertConversation(conversation);
              convId = conversation.id;
            }
          }

          // Bijlagen per doel apart uploaden — de server bindt een
          // bijlage aan precies één bericht, dus delen naar N
          // gesprekken = N uploads. Falende uploads slaan we over
          // (bericht gaat door), maar als ze allemaal falen markeren
          // we 't doel als mislukt.
          const attachmentIds: number[] = [];
          let uploadAttempts = 0;
          for (const file of share.files) {
            uploadAttempts += 1;
            try {
              const { attachment } = await uploadFile(file);
              attachmentIds.push(attachment.id);
            } catch {
              /* één rotte bijlage stopt 'n target niet */
            }
          }
          if (uploadAttempts > 0 && attachmentIds.length === 0 && body.length === 0) {
            failed.push({ name: t.name, reason: "geen bijlagen geüpload" });
            continue;
          }

          if (body.length > 0 || attachmentIds.length > 0) {
            await api.sendMessage(convId, { body, attachment_ids: attachmentIds });
            succeeded.add(t.key);
            lastConvId = convId;
          }
        } catch (err) {
          // Eén falend doel mag de overige niet stoppen — log + door.
          const msg = err instanceof Error ? err.message : "versturen mislukt";
          failed.push({ name: t.name, reason: msg });
        }
      }

      if (failed.length === 0) {
        // Alles geslaagd → kiezer sluiten en naar laatste gesprek.
        setPendingShare(null);
        if (lastConvId !== null) setActive(lastConvId);
      } else {
        // Vink succesvolle targets uit zodat een tweede klik op
        // "Sturen" alleen de overgebleven doelen herprobeert, zonder
        // dubbele berichten naar de gelukte ontvangers.
        setSelected((prev) => {
          const next = new Set(prev);
          for (const k of succeeded) next.delete(k);
          return next;
        });
        setProgress(
          `${succeeded.size} verstuurd, ${failed.length} mislukt: ${failed
            .map((f) => f.name)
            .join(", ")}`,
        );
      }
    } finally {
      setSending(false);
    }
  }

  const fileCount = share.files.length;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-900">
      {/* Kop */}
      <div
        className="flex shrink-0 items-center gap-3 border-b border-slate-700 bg-slate-800 px-3 py-3"
        style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
      >
        <button
          onClick={cancel}
          disabled={sending}
          className="flex h-9 w-9 items-center justify-center rounded text-slate-300 hover:bg-slate-700 disabled:opacity-50"
          aria-label="Annuleren"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-100">Delen met…</div>
          <div className="truncate text-xs text-slate-400">
            {fileCount > 0
              ? `${fileCount} bestand${fileCount === 1 ? "" : "en"}`
              : "Bericht"}
            {selected.size > 0 && ` · ${selected.size} gekozen`}
          </div>
        </div>
      </div>

      {/* Voorbeeld van gedeelde bestanden */}
      {fileCount > 0 && (
        <div className="flex shrink-0 flex-wrap gap-2 border-b border-slate-700 bg-slate-800/60 px-3 py-2">
          {share.files.map((f, i) => (
            <span
              key={i}
              className="flex max-w-[12rem] items-center gap-1 rounded bg-slate-700 px-2 py-1 text-xs text-slate-200"
            >
              <span className="shrink-0">📎</span>
              <span className="truncate">{f.name}</span>
              <span className="shrink-0 text-slate-400">{fileSize(f.size)}</span>
            </span>
          ))}
        </div>
      )}

      {/* Bijschrift */}
      <div className="shrink-0 border-b border-slate-700 bg-slate-800/60 px-3 py-2">
        <textarea
          className="w-full resize-none rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm leading-relaxed text-slate-100 outline-none focus:border-accent"
          rows={2}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder={fileCount > 0 ? "Voeg een bijschrift toe…" : "Bericht…"}
        />
      </div>

      {/* Zoekbalk */}
      <div className="shrink-0 px-3 py-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek kanaal of persoon…"
          className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 outline-none focus:border-accent"
        />
      </div>

      {/* Doellijst */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {shownChannels.length > 0 && (
          <>
            <SectionHeader label="Kanalen" />
            <ul>
              {shownChannels.map((t) => (
                <Row key={t.key} target={t} checked={selected.has(t.key)} onToggle={() => toggle(t.key)} />
              ))}
            </ul>
          </>
        )}
        {shownPeople.length > 0 && (
          <>
            <SectionHeader label="Gesprekken" />
            <ul>
              {shownPeople.map((t) => (
                <Row key={t.key} target={t} checked={selected.has(t.key)} onToggle={() => toggle(t.key)} />
              ))}
            </ul>
          </>
        )}
        {shownChannels.length === 0 && shownPeople.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-slate-500">
            Niets gevonden.
          </div>
        )}
      </div>

      {/* Verzendbalk */}
      <div
        className="flex shrink-0 items-center gap-3 border-t border-slate-700 bg-slate-800 px-3 py-3"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="min-w-0 flex-1 truncate text-xs text-slate-400">
          {progress ?? (selected.size === 0 ? "Kies één of meer bestemmingen" : `${selected.size} geselecteerd`)}
        </div>
        <button
          onClick={() => void doSend()}
          disabled={selected.size === 0 || sending}
          className="flex h-11 items-center gap-2 rounded-md bg-accent px-5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {sending ? "Versturen…" : "Sturen"}
          {!sending && (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </div>
  );
}

function Row({
  target,
  checked,
  onToggle,
}: {
  target: Target;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <li>
      <button
        onClick={onToggle}
        className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-slate-800 ${
          checked ? "bg-slate-800" : ""
        }`}
      >
        {target.kind === "conv" && target.presence === undefined ? (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-slate-500">#</span>
        ) : (
          <span
            className={`h-2.5 w-2.5 shrink-0 rounded-full ${presenceColor(
              target.presence ?? "offline",
            )}`}
          />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-slate-100">{target.name}</span>
          <span className="block truncate text-xs text-slate-500">{target.sub}</span>
        </span>
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
            checked ? "border-accent bg-accent text-white" : "border-slate-600 text-transparent"
          }`}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
      </button>
    </li>
  );
}
